import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
  model,
} from '@angular/core';

import { RingSpoke } from '../report.model';
import { ChartTheme, paletteFor } from './chart-palette';
import { TextAnchor, displayCode, polar, radialAnchor, round, sectorPath } from './chart-geometry';
import { ChartTooltipService, compactViewportSignal } from './chart-tooltip.service';

/** Écart angulaire entre deux rayons, en degrés. */
const PADDING = 1.2;
/** Marge externe reprise de Recharts (marge par défaut 5 de chaque côté). */
const OUTER_MARGIN = 10;

interface RingSpokeView {
  index: number;
  code: string;
  label: string;
  d: string;
  fill: string;
  opacity: string;
}

interface RingLabelView {
  index: number;
  x: number;
  y: number;
  anchor: TextAnchor;
  text: string;
  fill: string;
  weight: number;
}

/**
 * Anneau radial des compétences non acquises — un secteur par lacune.
 *
 * Tous les secteurs ont le même angle : ce graphique ne compare pas des
 * grandeurs, il donne une identité visuelle à chaque lacune, celle-là même que
 * reprennent la légende, le plan hebdomadaire et les puces du plan. La couleur
 * est donc porteuse de sens ; l'angle, non.
 *
 * **Pas de miroir en RTL**, contrairement aux graphiques cartésiens : une
 * roue n'a pas de sens de lecture, et la retourner casserait la correspondance
 * d'index avec la légende sans rien apporter.
 *
 * Le composant ne rend que le SVG ; `.ring__center` reste un frère dans le
 * gabarit de la section.
 */
@Component({
  selector: 's2e-ring-chart',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.display]': "'contents'" },
  template: `
    <svg
      class="chart-svg ring__svg"
      [attr.width]="size()"
      [attr.height]="size()"
      [attr.viewBox]="viewBox()"
      role="img"
      [attr.aria-label]="ariaLabel() || null"
      focusable="false"
      (mouseleave)="leave()"
    >
      <g class="ring__spokes">
        @for (spoke of spokeViews(); track spoke.code) {
          <path
            class="ring__spoke"
            [attr.data-index]="spoke.index"
            [attr.data-code]="spoke.code"
            [attr.d]="spoke.d"
            [attr.fill]="spoke.fill"
            [attr.stroke]="palette().surface"
            stroke-width="1"
            [attr.opacity]="spoke.opacity"
            (mouseenter)="enter(spoke, $event)"
            (mousemove)="tooltip.move($event)"
          ></path>
        }
      </g>
      <g class="ring__labels">
        @for (label of labelViews(); track label.index) {
          <text
            class="ring__label"
            [attr.data-index]="label.index"
            [attr.x]="label.x"
            [attr.y]="label.y"
            [attr.text-anchor]="label.anchor"
            dominant-baseline="middle"
            [attr.direction]="codeDir()"
            [attr.fill]="label.fill"
            font-size="11"
            [attr.font-weight]="label.weight"
          >{{ label.text }}</text>
        }
      </g>
    </svg>
  `,
})
export class RingChartComponent {
  readonly tooltip = inject(ChartTooltipService);

  readonly spokes = input.required<readonly RingSpoke[]>();
  readonly theme = input.required<ChartTheme>();

  /**
   * Direction de lecture de la page.
   *
   * Reçue pour que toutes les sections appellent leurs graphiques de la même
   * façon, mais **volontairement inutilisée** ici : voir la note de classe, la
   * roue ne se retourne pas.
   */
  readonly rtl = input.required<boolean>();

  /** Direction du texte des codes, dérivée du préfixe par la section. */
  readonly codeDir = input.required<'rtl' | 'ltr'>();

  /**
   * Index actif, en liaison bidirectionnelle.
   *
   * La légende, hors de ce composant, met en avant le même secteur — d'où le
   * `model()` plutôt qu'un état interne : les deux surfaces pilotent la même
   * valeur, dans les deux sens.
   */
  readonly activeIndex = model<number>(-1);

  /** Libellé accessible complet, déjà traduit par la section. */
  readonly ariaLabel = input('');

  /** Préfixe de code affiché (`C`, `م`…). `data-code` garde le code brut. */
  readonly codePrefix = input('C');

  /** Libellé de la ligne d'infobulle (« Statut »), déjà traduit. */
  readonly statusLabel = input('');
  /** Valeur de cette ligne (« Non acquise »), déjà traduite. */
  readonly statusValue = input('');

  private readonly compact = compactViewportSignal();

  /** 380 px en colonne étroite : à 520 les étiquettes sortiraient de la carte. */
  readonly size = computed(() => (this.compact() ? 380 : 520));
  readonly viewBox = computed(() => `0 0 ${this.size()} ${this.size()}`);
  readonly palette = computed(() => paletteFor(this.theme()));

  private readonly geometry = computed(() => {
    const size = this.size();
    const maxRadius = (size - OUTER_MARGIN) / 2;
    return {
      centre: size / 2,
      innerR: maxRadius * 0.3,
      outerR: maxRadius * 0.78,
    };
  });

  readonly spokeViews = computed<RingSpokeView[]>(() => {
    const data = this.spokes();
    if (data.length === 0) return [];

    const { centre, innerR, outerR } = this.geometry();
    const span = (360 - PADDING * data.length) / data.length;
    const active = this.activeIndex();

    return data.map((spoke, index) => {
      const start = 90 - index * (span + PADDING);
      const end = start - span;
      return {
        index,
        code: spoke.code,
        label: spoke.label,
        d: sectorPath(centre, centre, innerR, outerR, start, end),
        fill: spoke.color,
        opacity: active !== -1 && active !== index ? '0.28' : '1',
      };
    });
  });

  readonly labelViews = computed<RingLabelView[]>(() => {
    const data = this.spokes();
    if (data.length === 0) return [];

    const { centre, outerR } = this.geometry();
    const span = (360 - PADDING * data.length) / data.length;
    const active = this.activeIndex();
    const palette = this.palette();
    const prefix = this.codePrefix();

    return data.map((spoke, index) => {
      const start = 90 - index * (span + PADDING);
      const mid = start - span / 2;
      const point = polar(centre, centre, outerR + 16, mid);
      const isActive = active === index;
      return {
        index,
        x: round(point.x),
        y: round(point.y),
        anchor: radialAnchor(point.x, centre),
        text: displayCode(spoke.code, prefix),
        fill: isActive ? palette.textPrimary : palette.textSecondary,
        weight: isActive ? 700 : 600,
      };
    });
  });

  enter(spoke: RingSpokeView, event: MouseEvent): void {
    this.activeIndex.set(spoke.index);
    /* Code, nom, verdict. Ni pourcentage ni note : l'anneau porte des
       compétences, et une compétence n'en a pas. */
    const status = this.statusLabel();
    this.tooltip.show(
      `${displayCode(spoke.code, this.codePrefix())} · ${spoke.label}`,
      status === ''
        ? []
        : [{ label: status, value: this.statusValue(), color: spoke.fill }],
      event,
    );
  }

  leave(): void {
    this.activeIndex.set(-1);
    this.tooltip.hide();
  }
}
